import { CalendarOutlined, ExclamationCircleOutlined } from "@ant-design/icons"
import { Flex, Typography } from "antd"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import {
  type MilestoneDependency,
  MilestoneDependencyType
} from "../../../../../types/project-milestone-dependency.types.ts"
import { type ProjectMilestone } from "../../../../../types/project-phases.types.tsx"
import { useDateFormat } from "../../../../../../../hooks/use-date-format.ts"
import { DATE_FORMAT } from "../../../../../../../utils/date/date.ts"
import { lagPhrase } from "../../../../constants/milestone-dependency-relationship.ts"
import { calculateEffectiveSchedule } from "../../../../utils/milestone-effective-schedule.ts"
import { isMilestoneFinished } from "../../../../utils/milestone-schedule-state.ts"

const { Text } = Typography

const REASON_KEY: Record<MilestoneDependencyType, string> = {
  [MilestoneDependencyType.FINISH_TO_START]: "finishToStart",
  [MilestoneDependencyType.START_TO_START]: "startToStart",
  [MilestoneDependencyType.FINISH_TO_FINISH]: "finishToFinish",
  [MilestoneDependencyType.START_TO_FINISH]: "startToFinish"
}

/** Only a milestone the server will date from its dependencies gets a preview:
 * it has an Effort, is not finished, and has no dates of its own (none at all,
 * or the ones it has were written from its dependencies). A milestone with its
 * own dates is never moved by adding a dependency to it here. */
export const previewsDependencyDates = (milestone: ProjectMilestone): boolean =>
  (milestone.duration_days ?? 0) >= 1 &&
  !isMilestoneFinished(milestone) &&
  (!!milestone.dates_from_dependencies ||
    (!milestone.start_date && !milestone.end_date))

interface MilestoneDependencyDatePreviewProps {
  milestone: ProjectMilestone
  milestones: ProjectMilestone[]
  dependencies: MilestoneDependency[]
  sourceId: string
  type: MilestoneDependencyType
  lagDays: number
  nameOf: (id: string) => string
}

export function MilestoneDependencyDatePreview({
  milestone,
  milestones,
  dependencies,
  sourceId,
  type,
  lagDays,
  nameOf
}: MilestoneDependencyDatePreviewProps) {
  const { t } = useTranslation()
  const { parseDate, userFormat } = useDateFormat()

  const entry = useMemo(() => {
    const candidate: MilestoneDependency = {
      id: "__pending__",
      source_milestone: sourceId,
      target_milestone: milestone.id,
      type,
      type_display: "",
      lag_days: lagDays,
      created: "",
      updated: ""
    }
    const schedule = calculateEffectiveSchedule({
      milestones: [
        ...milestones.filter((m) => m.id !== milestone.id),
        milestone
      ],
      dependencies: [...dependencies, candidate],
      parseDate
    })
    return schedule.get(milestone.id) ?? null
  }, [milestone, milestones, dependencies, sourceId, type, lagDays, parseDate])

  if (!entry || !entry.shiftReason) {
    return (
      <Flex align="flex-start" gap={8} data-testid="dependency-date-preview">
        <ExclamationCircleOutlined
          style={{ color: "var(--ant-color-warning)", marginTop: 3 }}
        />
        <Text type="secondary" style={{ fontSize: 12.5 }}>
          {t("milestoneDependencies.preview.sourceUndated", {
            source: nameOf(sourceId),
            name: milestone.name || t("milestoneDependencies.fallbackMilestone")
          })}
        </Text>
      </Flex>
    )
  }

  const format = userFormat || DATE_FORMAT
  const winning = entry.shiftReason
  const reason = t(
    `milestoneDependencies.preview.reason.${REASON_KEY[winning.type]}`,
    {
      source: nameOf(winning.sourceMilestoneId)
    }
  )

  return (
    <Flex align="flex-start" gap={8} data-testid="dependency-date-preview">
      <CalendarOutlined
        style={{ color: "var(--ant-color-primary)", marginTop: 3 }}
      />
      <Text style={{ fontSize: 12.5 }}>
        {t("milestoneDependencies.preview.scheduled", {
          start: entry.start.format(format),
          end: entry.end.format(format),
          days: t("milestoneDependencies.preview.days", {
            count: entry.end.diff(entry.start, "day") + 1
          }),
          reason: winning.lagDays
            ? t("milestoneDependencies.preview.withLag", {
                reason,
                lag: lagPhrase(t, winning.lagDays)
              })
            : reason
        })}
      </Text>
    </Flex>
  )
}
